'use client';

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery } from '@tanstack/react-query';
import { searchGifs } from '@/components/textarea/actions/gifs-action';

/**
 * Core hook for fetching GIFs from Tenor with infinite scrolling support.
 * Uses React Query for automatic caching and pagination.
 * Results are persisted in IDB for offline access.
 *
 * @param search - The search query for GIFs
 * @returns React Query infinite query result
 */
export function useGifs(search) {
  const $ = _c(7);
  let t0;
  if ($[0] !== search) {
    t0 = ["gifs", search];
    $[0] = search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = t0 as const;
  let t2;
  if ($[2] !== search) {
    t2 = t3 => {
      const {
        pageParam: t4
      } = t3;
      const pageParam = t4 === undefined ? "" : t4;
      return searchGifs(search, pageParam);
    };
    $[2] = search;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: "" as string,
      getNextPageParam: _temp,
      staleTime: 300000
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useInfiniteQuery(t3);
}

/**
 * Simplified hook for consuming GIF search results with infinite scroll.
 * Provides a cleaner interface for components by extracting commonly used values.
 *
 * @param search - The search query for GIFs
 * @returns {object} Simplified interface containing:
 *   - gifs: Flattened array of all loaded GIFs
 *   - isLoading: Whether the initial fetch is in progress
 *   - isFetchingNextPage: Whether the next page is being fetched
 *   - hasNextPage: Whether there are more pages to load
 *   - fetchNextPage: Function to trigger loading the next page
 */
function _temp(lastPage) {
  return lastPage.next || undefined;
}
export function useGifsInfinite(search) {
  const $ = _c(8);
  const query = useGifs(search);
  let t0;
  if ($[0] !== query.data?.pages) {
    t0 = query.data?.pages.flatMap(_temp2) ?? [];
    $[0] = query.data?.pages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const gifs = t0;
  const isLoading = query.isLoading;
  const isFetchingNextPage = query.isFetchingNextPage;
  const hasNextPage = query.hasNextPage;
  const fetchNextPage = query.fetchNextPage;
  let t1;
  if ($[2] !== fetchNextPage || $[3] !== gifs || $[4] !== hasNextPage || $[5] !== isFetchingNextPage || $[6] !== isLoading) {
    t1 = {
      gifs,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage
    };
    $[2] = fetchNextPage;
    $[3] = gifs;
    $[4] = hasNextPage;
    $[5] = isFetchingNextPage;
    $[6] = isLoading;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function _temp2(page) {
  return page.results;
}